import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';

import Purchase from '../common/Purchase';
import BasicLayout from '../common/BasicLayout';
import { useCustomStripe } from '../common/Providers/CustomStripeProvider/UseCustomStripe';
import CheckoutForm from './CheckoutForm';
import { useResponsiveValues } from '../common/Providers/DimentionsProvider/UseResponsiveValues';
import { useStoreDetails } from '../common/Providers/StoreDetailsProvider/UseStoreDetails';
import { formatPrice, formatDateString } from '../common/Utils';
import { useCustomerReservation } from '../common/Providers/CustomerReservationProvider/UseCustomerReservation';
import { getClientSecret } from '../api/Stripe';
import { createReservationPre, updateReservationPre } from '../api/Product';
import BodhiProtectSelector from '../components/BodhiProtectSelector';

const CompletePurchase: React.FC = () => {
  
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { amount, clientSecret, stripePromise, setClientSecret, setAmount } = useCustomStripe();
  const { matches900 } = useResponsiveValues();
  const { storeDetails } = useStoreDetails();
  const { ReservationItems, ReservationMain, setReservationValue } = useCustomerReservation();

  const [isBodhiProtectActive, setIsBodhiProtectActive] = useState<boolean>(true);
  const intentCreated = useRef(false);
  const hasReserved = useRef(false);

  const createPaymentIntent = (finalTotal: number) => {
    if (intentCreated.current) { console.log('[v2] createPaymentIntent blocked - already created'); return; }
    intentCreated.current = true;
    console.log('[v2] createPaymentIntent called with finalTotal:', finalTotal);
    if (finalTotal <= 0) { navigate('/'); return; }
    const isValid = validateBeforeCheckout(ReservationMain, ReservationItems, enqueueSnackbar); // eslint-disable-line
    if (!isValid) return;
    const payload: any = {
      ...ReservationMain,
      amount: Math.round(finalTotal * 100),
      store_name: storeDetails.store_name,
      customerId: localStorage.getItem("customerId"),
      email: ReservationMain.email,
      items: ReservationItems.map((item: any) => ({
        display_name: item.display_name,
        family_id: item.family_id,
        quantity: item.quantity,
        price: item.price,
      })),
    };
    setAmount(finalTotal);
    setClientSecret(null);
    getClientSecret(payload, (jsonRes: any, status: any) => {
      if (status === 200) {
        console.log('[v2] Intent created:', jsonRes?.id, 'amount:', finalTotal);
        setClientSecret(jsonRes);
      } else {
        const errorMessage = jsonRes?.raw?.message ?? "Error occurred on the server.";
        showSnack(enqueueSnackbar, errorMessage);
      }
    });
  };

  useEffect(()=>{
    const accessToken = localStorage.getItem('access-token');
    if(!accessToken) navigate('/');

    if (ReservationMain.pickup && !hasReserved.current) {
      hasReserved.current = true;
      const forSavingOnDB: any = {
        brand_id: storeDetails.brand_id,
        start_date: formatDateString(ReservationMain.pickup),
        end_date: ReservationMain.dropoff ? formatDateString(ReservationMain.dropoff) : '',
        subtotal: ReservationMain.prices.subtotal,
        tax_rate: storeDetails.sales_tax,
        tax_amount: ReservationMain.prices.tax,
        discount_amount: ReservationMain.prices.discount,
        total_price: ReservationMain.prices.total,
        price_table_id: ReservationMain.price_table_id,
        stage: 2,
        address_id: ReservationMain.address_id,
        use_manual: ReservationMain.use_manual,
        manual_address: ReservationMain.manual_address,
        email: ReservationMain.email,
        phone_number: ReservationMain.phone_number,
        driver_tip: ReservationMain.driver_tip,
        customer_id: localStorage.getItem('customerId'),
        items: ReservationItems,
        discount_code: ReservationMain.discount_code,
        promo_code: ReservationMain.promo_code,
        note: ReservationMain.note,
      };
      createReservationPre(forSavingOnDB, (jsonRes: any, status: any) => {
        if (status === 201) localStorage.setItem('preId', jsonRes.reservation.id);
      });
    }
  }, []);

  useEffect(() => {
    const percentage = parseFloat(storeDetails.bodhi_protect_percentage || "0");
    const rawPrice = ReservationMain.prices.subtotal * (isNaN(percentage) ? 0 : percentage / 100);
    const bodhiProtectPrice = parseFloat(rawPrice.toFixed(2));

    let finalTotal = ReservationMain.prices.total;

    if (!storeDetails.is_bodhi_protect_enabled) {
      // No bodhi protect — use total as-is
      createPaymentIntent(finalTotal);
      return;
    }

    const defaultVal = !!storeDetails.bodhi_protect_default;
    setIsBodhiProtectActive(defaultVal);

    if (defaultVal) {
      setReservationValue("bodhi_protect_opted_out", false);
      setReservationValue("bodhi_protect_price", bodhiProtectPrice);
      // Compute final total ourselves — don't wait for async calcAndSetData
      finalTotal = ReservationMain.prices.total + bodhiProtectPrice;
    } else {
      setReservationValue("bodhi_protect_opted_out", true);
      setReservationValue("bodhi_protect_price", 0);
      // total already excludes bodhi protect
    }

    createPaymentIntent(finalTotal);
  }, [storeDetails.bodhi_protect_default, storeDetails.is_bodhi_protect_enabled]);

  const showSnack = (enqueueSnackbar: any, msg: string) => {
    enqueueSnackbar(msg, {
      variant: "error",
      style: { width: "350px" },
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  };

  const validateBeforeCheckout = (R: any, Items: any[], enqueueSnackbar: any) => {
    if (!R.pickup) {
      showSnack(enqueueSnackbar, "Please select pickup location");
      return false;
    }
    if (!R.dropoff) {
      showSnack(enqueueSnackbar, "Please select dropoff location");
      return false;
    }
    if (!Items.length) {
      showSnack(enqueueSnackbar, "Please select at least one item");
      return false;
    }
    if (!R.email) {
      showSnack(enqueueSnackbar, "Please provide email");
      return false;
    }
    if (!R.phone_number) {
      showSnack(enqueueSnackbar, "Please provide phone number");
      return false;
    }
    if (!R.is_accept) {
      showSnack(enqueueSnackbar, "Please accept the terms and policy");
      return false;
    }
    if (!R.address_id && !R.manual_address) {
      showSnack(enqueueSnackbar, "The reservation should have a delivery address");
      return false;
    }

    return true;
  };

  const bodhiProtectPrice = useMemo(() => {
    if (!ReservationMain?.prices?.subtotal) return 0;
    const percentage = parseFloat(storeDetails.bodhi_protect_percentage || "0");
    if (Number.isNaN(percentage)) return 0;
    return ReservationMain.prices.subtotal * (percentage / 100);
  }, [ReservationMain?.prices?.subtotal, storeDetails.bodhi_protect_percentage]);

  const bodhiProtectOptions = useMemo(() => {
    return [
      {
        value: true,
        label: `Yes, protect my booking (Add ${formatPrice(bodhiProtectPrice)})`,
      },
      {
        value: false,
        label: "No thanks, I'll take the risk",
      },
    ]
  }, [bodhiProtectPrice]);

  const appearanceOptions = {
    rules: {
      ".Input":{
        borderColor: "#cccccc"
      }
    },
    layout: {
      type: 'accordion',
      defaultCollapsed: true,
    }
  }

  const options = {
    clientSecret: clientSecret?.client_secret??'',
    appearance: appearanceOptions,
  };

  const renderCompletePurchase = () => (
    <BasicLayout>
      <Box sx={styles(matches900).container}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '50px', paddingTop: "44px", alignItems: 'center' }}>
          <Box sx={{width:'100%', maxWidth:'900px' }}>
            {!!storeDetails.is_bodhi_protect_enabled && (
              <BodhiProtectSelector
                options={bodhiProtectOptions}
                selectedValue={isBodhiProtectActive}
                primaryColor="#1976d2"
                onSelectYes={()=>{
                    if (isBodhiProtectActive) return;
                    setIsBodhiProtectActive(true);
                    setReservationValue("bodhi_protect_opted_out", false);
                    setReservationValue("bodhi_protect_price", bodhiProtectPrice);
                    const newTotal = ReservationMain.prices.total + bodhiProtectPrice;
                    intentCreated.current = false;
                    createPaymentIntent(newTotal);
                    const preId = localStorage.getItem('preId');
                    if (preId) updateReservationPre({ id: preId, bodhi_protect_opted_out: false, total_price: newTotal });
                }}
                onSelectNo={()=>{
                    if (!isBodhiProtectActive) return;
                    setIsBodhiProtectActive(false);
                    setReservationValue("bodhi_protect_opted_out", true);
                    setReservationValue("bodhi_protect_price", 0);
                    const newTotal = ReservationMain.prices.total - bodhiProtectPrice;
                    intentCreated.current = false;
                    createPaymentIntent(newTotal);
                    const preId = localStorage.getItem('preId');
                    if (preId) updateReservationPre({ id: preId, bodhi_protect_opted_out: true, total_price: newTotal });
                }}
              />
            )}

            {amount > 0 && clientSecret &&(
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm/>
              </Elements>
            )}
          </Box>
        </Box>
        <Purchase 
          title='Reservation Details' 
          buttonTitle="Complete Purchase"
          isDisableDriverTip={true}
          isDisableDiscount={true}
          // CancelOrder={()=>{
          //   navigate('/reservation');
          //   initReservation();
          //   enqueueSnackbar("Order Canceled", {
          //     variant: 'success',
          //     style: { width: '350px' },
          //     autoHideDuration: 3000,
          //     anchorOrigin: { vertical: 'top', horizontal: 'right' },
          //   })
          //   if(hasReserved.current == true){
              
          //   }
          // }}
        />
      </Box>
    </BasicLayout>
  );

  return renderCompletePurchase();
}

const styles = (matches900:boolean) => ({
  container: {
    display:'flex', 
    flexDirection: matches900? 'row': 'column',
  },
  ReservationMainDetail: {
    flex:1, 
    p:matches900?'60px 40px':'30px 24px',
    overflow: 'auto',
  },
});

export default CompletePurchase;
